import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, MapPin, CalendarDays, Users, Hotel, ShieldCheck, BadgePercent, Sparkles,
  MessagesSquare, MessageCircle, LineChart, FileText, Bot, Inbox, CheckCircle2, XCircle,
  Building2, Landmark, Home, Leaf, Ship, ArrowRight, Globe2, Network, Send, Heart,
} from "lucide-react";
import api from "../lib/api";
import { HPHeader } from "../components/hotelspulse/HPHeader";
import { HPFooter } from "../components/hotelspulse/HPFooter";

const NAVY = "#14304F";
const CORAL = "#F26749";
const TEAL = "#17A2A6";
const BORDER = "#E9DCC3";
const IMG = {
  hero: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1200&q=70",
  page: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=900&q=65",
  room: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=60",
  pool: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=60",
  spa: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&q=60",
  villa: "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=600&q=60",
  heritage: "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600&q=60",
  boat: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=600&q=60",
  city: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=60",
  mountain: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=60",
};

const PILLS = [["Boutique", "boutique_hotel"], ["Beach", "resort"], ["Heritage", "heritage"], ["Family", "hotel"], ["Wellness", "ayurveda_retreat"], ["Business", "hotel"], ["Airport", "hotel"], ["Houseboat", "houseboat"]];

const HOTEL_FEATURES = [
  [FileText, "AI-Assisted Property Page", "Create a rich, verified page with AI support."],
  [Bot, "Hotel-Approved AI Concierge", "Answers only from your approved information."],
  [Inbox, "Qualified Direct Leads", "Receive genuine traveler inquiries."],
  [MessageCircle, "WhatsApp & Email Responses", "Connect with travelers on your preferred channels."],
  [Send, "Direct Quote Builder", "Send personalized offers to travelers."],
  [LineChart, "Lead & Conversion Analytics", "Understand performance and measure results."],
];

const CATEGORIES = [
  ["Hotels & Resorts", IMG.pool, "hotel"], ["Homestays & Guesthouses", IMG.villa, "homestay"],
  ["Heritage Stays", IMG.heritage, "heritage"], ["Villas & Apartments", IMG.room, "villa"],
  ["Wellness & Ayurveda", IMG.spa, "ayurveda_retreat"], ["Houseboats & Eco-Stays", IMG.boat, "houseboat"],
];

const TEMPLATES = [
  ["Modern City", IMG.city, "A KINDER, BRIGHTER STAY"],
  ["Beach Resort", IMG.pool, "WAVES AT YOUR DOOR"],
  ["Mountain Retreat", IMG.mountain, "HIGHER PASTURES, BRIGHTER DAYS"],
  ["Garden Villa", IMG.villa, "GOOD PEOPLE, BETTER STAYS"],
  ["Heritage Courtyard", IMG.heritage, "A TIMELESS WELCOME"],
  ["Lakeside Eco-Stay", IMG.boat, "GREENER TRAVELS, BRIGHTER TOMORROWS"],
];

// Wide hero-banner styles (second collection supplied by the founder)
const BANNERS = [
  ["Airport Hotel", "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1000&q=60"],
  ["Tropical Pool Villa", "https://images.unsplash.com/photo-1540541338287-41700207dee6?w=1000&q=60"],
  ["Safari Lodge", "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1000&q=60"],
  ["Vineyard Estate", "https://images.unsplash.com/photo-1533105079780-92b9be482077?w=1000&q=60"],
  ["Desert Retreat", "https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=1000&q=60"],
  ["Overwater Bungalow", "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=1000&q=60"],
];

const Script = ({ children, className = "" }) => (
  <span className={`hp-script text-2xl leading-snug ${className}`} style={{ color: CORAL }}>{children}</span>
);

const SectionTitle = ({ children, sub, accent }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <h2 className="hp-display font-bold text-2xl sm:text-3xl tracking-tight" style={{ color: NAVY }}>{children}</h2>
      {sub && <p className="text-stone-500 mt-1 max-w-2xl">{sub}</p>}
    </div>
    {accent && <Script className="hidden md:block -rotate-2">{accent}</Script>}
  </div>
);

export default function HotelsPulse() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [s, setS] = useState({ where: "", check_in: "", check_out: "", guests: 2 });

  useEffect(() => {
    api.get("/directstay/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/directstay/properties?limit=6").then((r) => setFeatured(r.data.items || [])).catch(() => {});
  }, []);

  const search = (e) => {
    e.preventDefault();
    const sp = new URLSearchParams();
    if (s.where) sp.set("q", s.where);
    if (s.check_in) sp.set("checkin", s.check_in);
    if (s.check_out) sp.set("checkout", s.check_out);
    if (s.guests) sp.set("guests", s.guests);
    navigate(`/hotelspulse/stays?${sp}`);
  };

  return (
    <div data-testid="hotelspulse-landing" className="hp-paper">
      <HPHeader />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="hp-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]" style={{ color: NAVY }}>
              Distinctive Stays.<br /><span style={{ color: CORAL }}>Direct Connections.</span>
            </h1>
            <p className="text-stone-600 mt-4 text-lg max-w-lg">
              Discover independent hotels and request their best direct offer — with AI assistance before, during and after your stay.
            </p>

            <form onSubmit={search} data-testid="hero-search" className="mt-6 bg-white rounded-2xl shadow-lg border p-2 flex flex-wrap items-center gap-2" style={{ borderColor: BORDER, boxShadow: "0 12px 30px -12px rgba(20,48,79,0.18)" }}>
              <div className="flex items-center gap-2 flex-1 min-w-[160px] px-3 py-2 sm:border-r" style={{ borderColor: BORDER }}>
                <MapPin className="w-4 h-4 text-teal-700 shrink-0" />
                <input data-testid="search-where" className="w-full text-sm bg-transparent focus:outline-none" placeholder="Where are you going?" value={s.where} onChange={(e) => setS({ ...s, where: e.target.value })} />
              </div>
              <div className="flex items-center gap-1.5 shrink-0 px-2 py-2 sm:border-r" style={{ borderColor: BORDER }}>
                <CalendarDays className="w-4 h-4 text-teal-700 shrink-0" />
                <input data-testid="search-checkin" type="date" className="text-xs text-stone-500 bg-transparent focus:outline-none w-28" value={s.check_in} onChange={(e) => setS({ ...s, check_in: e.target.value })} />
              </div>
              <div className="flex items-center gap-1.5 shrink-0 px-2 py-2 sm:border-r" style={{ borderColor: BORDER }}>
                <CalendarDays className="w-4 h-4 text-teal-700 shrink-0" />
                <input data-testid="search-checkout" type="date" className="text-xs text-stone-500 bg-transparent focus:outline-none w-28" value={s.check_out} onChange={(e) => setS({ ...s, check_out: e.target.value })} />
              </div>
              <div className="flex items-center gap-1.5 shrink-0 px-2 py-2">
                <Users className="w-4 h-4 text-teal-700 shrink-0" />
                <input type="number" min="1" className="text-sm w-10 bg-transparent focus:outline-none" value={s.guests} onChange={(e) => setS({ ...s, guests: e.target.value })} />
              </div>
              <button data-testid="search-submit" className="rounded-xl px-5 py-3 text-sm font-700 text-white inline-flex items-center justify-center gap-2 whitespace-nowrap flex-1 min-w-[150px] transition-opacity hover:opacity-90" style={{ background: TEAL }}>
                <Search className="w-4 h-4" /> Search Stays
              </button>
            </form>

            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/hotelspulse/stays" data-testid="cta-browse-stays" className="rounded-lg px-6 py-3 text-sm font-700 text-white transition-opacity hover:opacity-90" style={{ background: CORAL }}>Explore Hotels</Link>
              <Link to="/hotelspulse/dashboard" data-testid="cta-list-property" className="rounded-lg px-6 py-3 text-sm font-700 border transition-colors hover:bg-white" style={{ borderColor: NAVY, color: NAVY }}>List Your Property</Link>
            </div>
            <p className="text-xs text-stone-400 mt-4">Verified property information · Direct hotel response · No booking commission added by HotelsPulse</p>
            {config?.pilot_free && (
              <div data-testid="pilot-banner" className="mt-3 inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-1.5 text-xs font-600 text-teal-800">
                <CheckCircle2 className="w-3.5 h-3.5" /> Founding-partner pilot: every hotel plan is free while we launch across Kerala & India
              </div>
            )}
          </div>
          <div className="relative hidden lg:block">
            <img src={IMG.hero} alt="Independent resort welcome" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
            <div className="absolute -bottom-5 right-6 bg-white rounded-2xl shadow-lg border p-4 w-72" style={{ borderColor: BORDER }}>
              <div className="flex items-center gap-2 text-sm font-700" style={{ color: NAVY }}><Sparkles className="w-4 h-4 text-teal-600" /> Ask HotelsPulse AI</div>
              <p className="text-xs text-stone-500 mt-1">"Find me a quiet waterfront stay for a family of four."</p>
            </div>
            <Script className="absolute top-4 right-6 bg-white/85 rounded-lg px-3 py-1 rotate-2">A more meaningful way to stay</Script>
          </div>
        </div>
      </section>

      {/* Better connection trio */}
      <section className="bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-center mb-8">
            <h2 className="hp-display font-bold text-2xl sm:text-3xl tracking-tight" style={{ color: NAVY }}>A Better Connection in Hospitality</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 relative">
            {[[Globe2, "Travelers", "Discover, compare and connect directly."],
              [Building2, "Independent Hotels", "Own your story and receive qualified leads."],
              [Users, "Local Communities", "Keep more travel value in the destination."]].map(([Icon, t, b]) => (
              <div key={t} className="border rounded-2xl hp-paper p-6 text-center hover:shadow-md transition-shadow" style={{ borderColor: BORDER }}>
                <Icon className="w-8 h-8 mx-auto text-teal-700 mb-3" />
                <div className="hp-display font-bold" style={{ color: NAVY }}>{t}</div>
                <p className="text-sm text-stone-500 mt-1">{b}</p>
              </div>
            ))}
            <Script className="hidden lg:block absolute -right-2 -bottom-8 -rotate-3">Good Stays<br />Brighter Places</Script>
          </div>
        </div>
      </section>

      {/* Find a stay + pills + featured */}
      <section id="stay-types" className="hp-band border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <SectionTitle sub accent="Different stays. A more connected world.">Find a Stay That Fits Your Journey</SectionTitle>
          <div className="flex flex-wrap gap-2 mb-8">
            {PILLS.map(([label, type], i) => (
              <Link key={label} to={`/hotelspulse/stays?type=${type}`} data-testid={`pill-${label.toLowerCase()}`}
                className={`rounded-full px-4 py-1.5 text-sm font-600 border transition-colors ${i === 0 ? "text-white border-teal-700" : "bg-white text-stone-600 hover:border-teal-500"}`}
                style={i === 0 ? { background: TEAL } : { borderColor: BORDER }}>{label}</Link>
            ))}
          </div>
          {featured.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((p) => (
                <Link key={p.id} to={`/hotelspulse/stay/${p.slug}`} data-testid={`featured-${p.slug}`} className="bg-white rounded-2xl border shadow-sm overflow-hidden group hover:shadow-md transition-shadow" style={{ borderColor: BORDER }}>
                  <div className="relative">
                    {p.photos?.[0] ? <img src={p.photos[0]} alt={p.name} className="w-full h-44 object-cover" /> : <div className="w-full h-44 hp-band flex items-center justify-center"><Hotel className="w-10 h-10 text-stone-300" /></div>}
                    <button className="absolute top-3 right-3 bg-white/90 rounded-full p-1.5"><Heart className="w-4 h-4 text-stone-400" /></button>
                  </div>
                  <div className="p-4">
                    <div className="hp-display font-bold group-hover:text-teal-700 transition-colors" style={{ color: NAVY }}>{p.name}</div>
                    <div className="text-xs text-stone-500 capitalize">{(p.property_type || "").replace(/_/g, " ")}</div>
                    <div className="text-xs text-stone-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {p.location?.city}, {p.location?.state}</div>
                    <div className="flex gap-2 mt-3">
                      <span className="inline-flex items-center gap-1 text-[10px] font-700 text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5"><ShieldCheck className="w-3 h-3" /> Verified</span>
                      {p.offers_count > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-700 rounded-full px-2 py-0.5 border" style={{ color: CORAL, borderColor: "#f8c4b5", background: "#fef3ef" }}><BadgePercent className="w-3 h-3" /> Direct Offer</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-dashed rounded-2xl p-12 text-center text-stone-500" style={{ borderColor: "#D9C9A8" }}>
              Founding partners are being onboarded now — verified stays appear here as they go live.
            </div>
          )}
        </div>
      </section>

      {/* Your hotel page powered to perform */}
      <section id="for-hotels" className="bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <SectionTitle sub="Showcase your property. Attract the right guests. Receive direct inquiries." accent="Your story deserves direct guests.">Your Hotel Page, Powered to Perform</SectionTitle>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="hp-paper rounded-2xl border shadow-lg p-4" style={{ borderColor: BORDER }}>
              <div className="flex items-center gap-1.5 mb-3">{["#f87171", "#fbbf24", "#34d399"].map((c) => <span key={c} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />)}</div>
              <div className="grid grid-cols-3 gap-3">
                <img src={IMG.page} alt="Property page preview" className="col-span-2 rounded-xl h-40 object-cover" />
                <div className="bg-white rounded-xl p-3 border" style={{ borderColor: BORDER }}>
                  <div className="text-[10px] font-700 uppercase text-stone-400">Request Best Direct Rate</div>
                  <div className="mt-2 space-y-1.5">{["Check-in", "Check-out", "Guests"].map((x) => <div key={x} className="hp-paper border rounded px-2 py-1 text-[10px] text-stone-400" style={{ borderColor: BORDER }}>{x}</div>)}</div>
                  <div className="mt-2 rounded px-2 py-1.5 text-center text-[10px] font-700 text-white" style={{ background: CORAL }}>Send Inquiry to Hotel</div>
                </div>
                {[IMG.room, IMG.pool, IMG.spa].map((src, i) => <img key={i} src={src} alt="Room" className="rounded-lg h-20 w-full object-cover" />)}
              </div>
              <div className="mt-3 text-xs text-stone-400">Rooms · Amenities · Location · Nearby Attractions · AI Concierge · Contact</div>
            </div>
            <div>
              <div className="space-y-4">
                {HOTEL_FEATURES.map(([Icon, t, b]) => (
                  <div key={t} className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-teal-700" /></span>
                    <div><div className="font-700 text-sm" style={{ color: NAVY }}>{t}</div><p className="text-xs text-stone-500">{b}</p></div>
                  </div>
                ))}
              </div>
              <a href="#plans" data-testid="see-plans-btn" className="inline-block mt-6 rounded-lg px-6 py-3 text-sm font-700 text-white transition-opacity hover:opacity-90" style={{ background: CORAL }}>See Hotel Partner Plans</a>
            </div>
          </div>
        </div>
      </section>

      {/* Page style templates */}
      <section id="page-styles" className="hp-paper border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <SectionTitle sub="Twelve signature page styles — pick the one that matches your property's personality." accent="Your page. Your style.">Custom Pages, Ready for Your Story</SectionTitle>
          <div className="grid md:grid-cols-2 gap-5">
            {TEMPLATES.map(([name, img, slogan]) => (
              <div key={name} data-testid={`template-${name.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white rounded-2xl border shadow-sm overflow-hidden flex hover:shadow-md transition-shadow" style={{ borderColor: BORDER }}>
                <div className="p-5 flex flex-col justify-center w-1/2 shrink-0">
                  <div className="flex items-center gap-1.5 text-[11px] font-700" style={{ color: NAVY }}>
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-white" style={{ background: NAVY }}><Hotel className="w-2.5 h-2.5" /></span>
                    HotelsPulse<span className="text-teal-700 -ml-1">.pro</span>
                  </div>
                  <span className="block w-8 h-0.5 my-2" style={{ background: CORAL }} />
                  <div className="hp-display font-black text-lg uppercase leading-tight" style={{ color: NAVY }}>Property Name</div>
                  <div className="text-xs text-stone-500 mt-0.5">Stay Somewhere Memorable</div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <span className="rounded px-2.5 py-1 text-[10px] font-700 text-white" style={{ background: TEAL }}>Explore Rooms</span>
                    <span className="rounded px-2.5 py-1 text-[10px] font-700 text-white" style={{ background: CORAL }}>Request Direct Rate</span>
                  </div>
                  <div className="text-[10px] font-600 text-stone-400 mt-3 uppercase tracking-wide">{name} style</div>
                </div>
                <div className="relative w-1/2">
                  <img src={img} alt={`${name} page style`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white via-white/20 to-transparent" />
                  <span className="absolute top-3 right-3 text-white text-[9px] font-700 tracking-[0.2em] [writing-mode:vertical-rl] drop-shadow">{slogan}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-stone-400 mt-6 text-center">Every style is fully editable — photos, colors and sections adapt to your property when you build your page.</p>

          {/* Wide hero-banner collection */}
          <div className="flex items-end justify-between gap-4 mt-14 mb-6">
            <div>
              <h3 className="hp-display font-bold text-xl sm:text-2xl tracking-tight" style={{ color: NAVY }}>The Banner Collection</h3>
              <p className="text-stone-500 text-sm mt-1">Full-width hero banners — your story front and center, from airport hotels to overwater bungalows.</p>
            </div>
            <Script className="hidden md:block -rotate-2">Your Stay, Your Story.</Script>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {BANNERS.map(([name, img]) => (
              <div key={name} data-testid={`banner-${name.toLowerCase().replace(/\s+/g, "-")}`} className="relative rounded-2xl border shadow-sm overflow-hidden h-44 sm:h-48 bg-white hover:shadow-md transition-shadow" style={{ borderColor: BORDER }}>
                <img src={img} alt={`${name} banner style`} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-right" />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-transparent" />
                <div className="relative h-full flex flex-col justify-center p-5 w-3/5">
                  <div className="flex items-center gap-1.5 text-[11px] font-700" style={{ color: NAVY }}>
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-white" style={{ background: NAVY }}><Hotel className="w-2.5 h-2.5" /></span>
                    HotelsPulse<span className="text-teal-700 -ml-1">.pro</span>
                  </div>
                  <div className="hp-display font-black text-xl sm:text-2xl uppercase leading-tight mt-2" style={{ color: NAVY }}>Property Name</div>
                  <div className="text-xs text-stone-500 mt-0.5">Your Stay, Your Story</div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <span className="rounded px-3 py-1.5 text-[10px] font-700 text-white" style={{ background: CORAL }}>View Rooms</span>
                    <span className="rounded px-3 py-1.5 text-[10px] font-700 border bg-white/80" style={{ borderColor: TEAL, color: TEAL }}>Request Direct Rate</span>
                  </div>
                  <div className="text-[10px] font-600 text-stone-400 mt-2.5 uppercase tracking-wide">{name} style</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI that knows the property */}
      <section className="hp-band border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="hp-display font-bold text-2xl sm:text-3xl tracking-tight" style={{ color: NAVY }}>AI That Knows the Property</h2>
            <p className="text-stone-500 mt-1 mb-6">Get instant, accurate answers from hotel-approved information.</p>
            <div className="bg-white rounded-2xl border shadow-sm p-5 space-y-3 max-w-lg" style={{ borderColor: BORDER }}>
              <div className="flex justify-end"><div className="hp-paper rounded-2xl rounded-tr-sm px-4 py-2 text-sm border" style={{ color: NAVY, borderColor: BORDER }}>Do you have a family room and airport pickup?</div></div>
              <div className="flex gap-2">
                <span className="w-8 h-8 rounded-full text-white flex items-center justify-center shrink-0" style={{ background: TEAL }}><Bot className="w-4 h-4" /></span>
                <div className="bg-stone-50 border border-stone-100 rounded-2xl rounded-tl-sm px-4 py-2 text-sm text-stone-600">The hotel lists two family-room options and offers airport transfers on request. Share your dates and the property will confirm availability and price.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {["Uses Hotel-Approved Information", "Multilingual", "Available 24/7"].map((b) => (
                <span key={b} className="inline-flex items-center gap-1 text-xs font-600 text-teal-800 bg-teal-50 border border-teal-200 rounded-full px-3 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> {b}</span>
              ))}
            </div>
            <p className="text-[11px] text-stone-400 mt-3">Availability and rates are confirmed by the property. Concierge rolls out to Growth-plan partners first.</p>
          </div>
          <div className="relative hidden lg:block">
            <img src={IMG.villa} alt="Traveler planning a stay" className="rounded-3xl w-full h-80 object-cover shadow-lg" />
            <Script className="absolute -bottom-6 right-8 rotate-2">Real information.<br />More confident travel.</Script>
          </div>
        </div>
      </section>

      {/* Network of discovery */}
      <section className="bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <SectionTitle sub="Your hotel reaches travelers across our growing network." accent="Local stays. Global travelers.">One Page. A Network of Discovery.</SectionTitle>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="flex items-center gap-6">
              <div className="bg-white rounded-2xl border-2 shadow-md p-3 w-48 shrink-0" style={{ borderColor: TEAL }}>
                <img src={IMG.pool} alt="Hotel page" className="rounded-xl h-24 w-full object-cover" />
                <div className="text-xs font-700 text-center mt-2" style={{ color: NAVY }}>Your Hotel Page</div>
              </div>
              <Network className="w-8 h-8 shrink-0" style={{ color: TEAL }} />
              <div className="space-y-2 flex-1">
                {["TravelerPulse", "KeralaTravels", "State Portals", "District & City Portals", "Airport Pages", "Travel Categories"].map((n) => (
                  <div key={n} className="hp-paper border rounded-lg px-3 py-1.5 text-sm font-600 text-stone-600" style={{ borderColor: BORDER }}>{n}</div>
                ))}
              </div>
            </div>
            <div className="hp-paper border rounded-2xl shadow-sm p-8" style={{ borderColor: BORDER }}>
              <div className="hp-display font-bold text-xl" style={{ color: NAVY }}>Every inquiry returns to one hotel lead inbox.</div>
              <p className="text-stone-500 mt-2">More visibility. More relevant travelers. More direct opportunities — with transparent source attribution for every lead.</p>
              <Link to="/hotelspulse/dashboard" className="inline-flex items-center gap-2 mt-4 text-sm font-700 text-teal-700 hover:underline">See how distribution works <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Discovery to direct lead */}
      <section id="how-it-works" className="hp-band border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <SectionTitle sub="A simple journey. A more rewarding outcome.">From Discovery to Direct Lead</SectionTitle>
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[[Search, "Traveler Discovers", "Finds your property on HotelsPulse or our partner network."],
                [Bot, "AI Answers", "Gets instant, accurate information from your approved content."],
                [Send, "Traveler Requests", "Sends an inquiry or quote request."],
                [MessagesSquare, "Hotel Responds", "Shares a direct offer and continues the conversation."]].map(([Icon, t, b], i) => (
                <div key={t} className="text-center">
                  <span className="w-12 h-12 rounded-full mx-auto flex items-center justify-center text-white mb-3" style={{ background: i % 2 ? TEAL : CORAL }}><Icon className="w-5 h-5" /></span>
                  <div className="text-sm font-700" style={{ color: NAVY }}>{i + 1}. {t}</div>
                  <p className="text-xs text-stone-500 mt-1">{b}</p>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border shadow-sm p-5" style={{ borderColor: BORDER }}>
              <div className="text-sm font-700 mb-3" style={{ color: NAVY }}>Hotel Lead Dashboard</div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["New Lead", 3, "bg-amber-50 border-amber-200"], ["Qualified", 5, "bg-teal-50 border-teal-200"], ["Quote Sent", 4, "bg-orange-50 border-orange-200"], ["Booked", 2, "bg-emerald-50 border-emerald-200"]].map(([k, v, c]) => (
                  <div key={k} className={`border rounded-xl py-3 ${c}`}>
                    <div className="hp-display font-black text-2xl" style={{ color: NAVY }}>{v}</div>
                    <div className="text-[10px] text-stone-500 font-600">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <SectionTitle sub="Extraordinary places. A fairer, more connected future." accent="Independent Hotels. Stronger Together.">Built for Independent Hospitality</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map(([label, img, type]) => (
              <Link key={label} to={`/hotelspulse/stays?type=${type}`} data-testid={`category-${type}`} className="group">
                <img src={img} alt={label} className="rounded-xl h-28 w-full object-cover group-hover:opacity-90 transition-opacity" />
                <div className="text-xs font-700 mt-2 text-center" style={{ color: NAVY }}>{label}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison + plans */}
      <section id="plans" className="hp-band border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <SectionTitle sub="A more sustainable way to grow your business." accent="More direct guests. A brighter tomorrow.">Grow Direct — Without Giving Away Every Reservation</SectionTitle>
          <div className="grid md:grid-cols-2 gap-4 mb-10">
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="hp-display font-bold mb-3 text-stone-500">Large Portal Model</div>
              {["Percentage commissions on completed stays", "Limited guest relationship", "Crowded comparison with many similar listings"].map((x) => (
                <div key={x} className="flex items-start gap-2 text-sm text-stone-500 py-1"><XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" /> {x}</div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border-2 p-6" style={{ borderColor: TEAL }}>
              <div className="hp-display font-bold mb-3" style={{ color: NAVY }}>HotelsPulse Direct Lead Model</div>
              {["Selectable monthly plan or pay-per-qualified-lead option", "Direct hotel response to traveler inquiries", "Hotel owns the guest relationship", "Transparent attribution across our network"].map((x) => (
                <div key={x} className="flex items-start gap-2 text-sm text-stone-600 py-1"><CheckCircle2 className="w-4 h-4 text-teal-700 shrink-0 mt-0.5" /> {x}</div>
              ))}
            </div>
          </div>
          {config && (
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(config.tiers).map(([key, t]) => (
                <div key={key} data-testid={`tier-${key}`} className={`bg-white rounded-2xl p-6 ${key === "growth" ? "border-2" : "border"}`} style={key === "growth" ? { borderColor: CORAL } : { borderColor: BORDER }}>
                  <div className="hp-display font-bold text-lg" style={{ color: NAVY }}>{t.name}</div>
                  <div className="mt-1 mb-3">
                    {config.pilot_free ? (
                      <><span className="text-xl font-900 text-teal-700">Free</span>{t.price_inr_month > 0 && <span className="text-xs text-stone-400 line-through ml-2">₹{t.price_inr_month}/mo</span>}<span className="block text-[10px] font-600" style={{ color: CORAL }}>during the pilot</span></>
                    ) : <span className="text-xl font-900" style={{ color: NAVY }}>{t.price_inr_month ? `₹${t.price_inr_month}/mo` : "Free"}</span>}
                  </div>
                  <ul className="space-y-1.5">{t.features.slice(0, 5).map((f) => <li key={f} className="text-xs text-stone-500 flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-teal-700 shrink-0" /> {f}</li>)}</ul>
                </div>
              ))}
            </div>
          )}
          <div className="text-center mt-8">
            <Link to="/hotelspulse/dashboard" data-testid="cta-list-my-property" className="inline-block rounded-lg px-8 py-3.5 text-sm font-700 text-white transition-opacity hover:opacity-90" style={{ background: CORAL }}>List My Property</Link>
          </div>
        </div>
      </section>

      {/* Start Connect Grow */}
      <section className="bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
          <SectionTitle sub="Everything you need to attract more of the right guests.">Start. Connect. Grow.</SectionTitle>
          <div className="grid sm:grid-cols-3 gap-4">
            {[[Home, "Create", "Claim and build a verified property page."],
              [Inbox, "Connect", "Receive and respond to qualified travelers."],
              [LineChart, "Grow", "Measure sources, offers and reported bookings."]].map(([Icon, t, b]) => (
              <div key={t} className="border rounded-2xl hp-paper p-6" style={{ borderColor: BORDER }}>
                <Icon className="w-7 h-7 text-teal-700 mb-3" />
                <div className="hp-display font-bold" style={{ color: NAVY }}>{t}</div>
                <p className="text-sm text-stone-500 mt-1">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dark CTA band */}
      <section className="text-white" style={{ background: NAVY }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 text-center">
          <h2 className="hp-display font-bold text-2xl sm:text-3xl tracking-tight">Let Travelers Discover What Makes Your Property Special.</h2>
          <p className="text-stone-300 mt-2">Create your HotelsPulse page and start building more direct guest relationships.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/hotelspulse/dashboard" data-testid="cta-bottom" className="rounded-lg px-7 py-3.5 text-sm font-700 text-white transition-opacity hover:opacity-90" style={{ background: CORAL }}>List Your Property</Link>
            <Link to="/hotelspulse/stays" className="rounded-lg px-7 py-3.5 text-sm font-700 border border-stone-400 text-stone-100 hover:bg-white/10 transition-colors">Find a Stay</Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs text-stone-400">
            <Landmark className="w-3.5 h-3.5" /> Powered by TravelerPulse and the SocialPulse network
            <Ship className="w-3.5 h-3.5 ml-3" /> <Leaf className="w-3.5 h-3.5" /> Distinctive Stays. A Brighter Tomorrow.
          </div>
        </div>
      </section>
      <HPFooter />
    </div>
  );
}
